import React, { useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useInvoiceSidebar } from '../useInvoiceSidebar';
import { SidebarAllocation, SidebarPriorInvoice, formatCurrency, formatDate } from '../../../config';
import './left-sidebar.scss';

type LeftTab = 'time' | 'invoices';

const LEFT_TABS: Array<{ id: LeftTab; label: string; icon: React.ReactNode }> = [
  {
    id: 'time',
    label: 'Time',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8" cy="8" r="6.5" />
        <path d="M8 4.5V8l2.5 1.5" />
      </svg>
    ),
  },
  {
    id: 'invoices',
    label: 'Invoices',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 1.5h10v13l-2-1.2-1.5 1.2L8 13.3 6.5 14.5 5 13.3 3 14.5z" />
        <line x1="5.5" y1="5" x2="10.5" y2="5" />
        <line x1="5.5" y1="8" x2="10.5" y2="8" />
      </svg>
    ),
  },
];

interface LeftSidebarProps {
  invoiceId: string | undefined;
  /** Current invoice's live values — included in the Invoices tab so totals
   * update as the user edits this invoice. */
  currentInvoice: SidebarPriorInvoice | null;
  /** Called after Bundle / Unbundle so the editor can refetch invoice detail
   * (combined line items + status changes). */
  onBundlingChanged?: () => void;
  /** Active tab is owned by the parent InvoiceEditor so it survives the
   * loading-state remount cycle when navigating between sibling invoices.
   * Without this the user is bounced back to the Time tab every click. */
  activeTab: LeftTab;
  onActiveTabChange: (tab: LeftTab) => void;
}

interface EmployeeAggregate {
  employee_id: string;
  employee_name: string;
  hours: number;
  total_value: number;
}

function aggregateByEmployee(allocations: SidebarAllocation[]): EmployeeAggregate[] {
  const map = new Map<string, EmployeeAggregate>();
  for (const a of allocations) {
    const existing = map.get(a.employee_id);
    if (existing) {
      existing.hours += a.hours;
      existing.total_value += a.total_value;
    } else {
      map.set(a.employee_id, {
        employee_id: a.employee_id,
        employee_name: a.employee_name,
        hours: a.hours,
        total_value: a.total_value,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.employee_name.localeCompare(b.employee_name));
}

interface TypeAggregate {
  employee_type: string;
  hours: number;
  total_value: number;
}

function aggregateByType(allocations: SidebarAllocation[]): TypeAggregate[] {
  const map = new Map<string, TypeAggregate>();
  for (const a of allocations) {
    const key = a.employee_type ?? 'Other';
    const existing = map.get(key);
    if (existing) {
      existing.hours += a.hours;
      existing.total_value += a.total_value;
    } else {
      map.set(key, { employee_type: key, hours: a.hours, total_value: a.total_value });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.employee_type.localeCompare(b.employee_type));
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({ invoiceId, currentInvoice, onBundlingChanged, activeTab, onActiveTabChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const supabase = useSupabaseClient();
  const { allocations, priorInvoices, loading, refetch } = useInvoiceSidebar(invoiceId);

  const [collapsed, setCollapsed] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const overallTotals = useMemo(() => {
    let hours = 0;
    let amount = 0;
    for (const a of allocations) {
      hours += a.hours;
      amount += a.total_value;
    }
    return { hours, amount };
  }, [allocations]);

  const employeeAggregates = useMemo(() => aggregateByEmployee(allocations), [allocations]);
  const typeAggregates = useMemo(() => aggregateByType(allocations), [allocations]);

  // All invoices on this project/stage = prior invoices + the current invoice
  // (current carries live edited totals so the section updates in real time).
  // Sorted by invoice_number DESC so the most recent shows first.
  const allInvoices = useMemo(() => {
    const merged = currentInvoice ? [...priorInvoices, currentInvoice] : [...priorInvoices];
    return merged.sort((a, b) => b.invoice_number.localeCompare(a.invoice_number, undefined, { numeric: true }));
  }, [priorInvoices, currentInvoice]);

  // Display ex-GST values on this panel. Fall back to dividing total by 1.1
  // for legacy rows that come back without an our_fee field.
  const exGstAmount = useCallback(
    (inv: SidebarPriorInvoice) => inv.our_fee ?? inv.total_amount / 1.1,
    []
  );

  const invoiceStatusAggregates = useMemo(() => {
    const map = new Map<string, number>();
    for (const inv of allInvoices) {
      map.set(inv.status, (map.get(inv.status) ?? 0) + exGstAmount(inv));
    }
    return Array.from(map.entries())
      .map(([status, amount]) => ({ status, amount }))
      .sort((a, b) => a.status.localeCompare(b.status));
  }, [allInvoices, exGstAmount]);

  const invoiceTotal = useMemo(
    () => allInvoices.reduce((sum, inv) => sum + exGstAmount(inv), 0),
    [allInvoices, exGstAmount]
  );

  const selectedAllocations = useMemo(
    () => allocations.filter(a => selected.has(a.id)),
    [allocations, selected]
  );
  const selectedTotals = useMemo(() => {
    let hours = 0;
    let amount = 0;
    for (const a of selectedAllocations) {
      hours += a.hours;
      amount += a.total_value;
    }
    return { hours, amount };
  }, [selectedAllocations]);
  const selectedEmployeeAggregates = useMemo(
    () => aggregateByEmployee(selectedAllocations),
    [selectedAllocations]
  );

  const toggleSelected = useCallback((id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const handlePriorInvoiceClick = useCallback((id: number) => {
    // Carry the return address along, so Close on the next invoice still goes
    // back to the page the user originally came from.
    navigate(`/invoices/${id}`, { state: location.state });
  }, [navigate, location.state]);

  const setBundling = useCallback(async (priorId: number, targetId: number | null) => {
    const { error } = await supabase.rpc('set_invoice_bundling', {
      p_invoice_id: priorId,
      p_bundled_in_invoice_id: targetId,
    });
    if (error) {
      console.error('Error updating bundling:', error);
      return;
    }
    await refetch();
    onBundlingChanged?.();
  }, [supabase, refetch, onBundlingChanged]);

  const currentInvoiceId = currentInvoice?.id ?? null;

  return (
    <div className={`invoice-editor__sidebar invoice-editor__sidebar--left ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Expand panel' : 'Collapse panel'}
          aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1.5" y="2.5" width="13" height="11" rx="2" />
            <line x1="6" y1="2.5" x2="6" y2="13.5" />
          </svg>
        </button>
        {!collapsed && (
          <div className="sidebar-tabs" role="tablist">
            {LEFT_TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                title={tab.label}
                className={`sidebar-tab${activeTab === tab.id ? ' sidebar-tab--active' : ''}`}
                onClick={() => onActiveTabChange(tab.id)}
              >
                {tab.icon}
                <span className="sidebar-tab__label">{tab.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {collapsed && (
        <div className="sidebar-rail">
          {LEFT_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`rail-btn ${activeTab === tab.id ? 'active' : ''}`}
              title={tab.label}
              aria-label={tab.label}
              onClick={() => { onActiveTabChange(tab.id); setCollapsed(false); }}
            >
              {tab.icon}
            </button>
          ))}
        </div>
      )}

      {!collapsed && (
        <div className="sidebar-content">
          {activeTab === 'time' && (
            <div className="sidebar-section">
              <h3 className="sidebar-section__title">Time Allocations</h3>
              {loading ? (
                <p className="sidebar-section__hint">Loading…</p>
              ) : allocations.length === 0 ? (
                <p className="sidebar-section__hint">No allocated time on this project/stage yet.</p>
              ) : (
                <>
                  <div className="totals-card">
                    {typeAggregates.map(t => (
                      <div key={t.employee_type} className="totals-card__group totals-card__group--vertical">
                        <div className="totals-card__label">{t.employee_type}</div>
                        <div className="totals-card__values">
                          <span className="totals-card__hours">{t.hours.toFixed(2)} hrs</span>
                          <span className="totals-card__amount">{formatCurrency(t.total_value)}</span>
                        </div>
                      </div>
                    ))}
                    <div className="totals-card__group totals-card__group--vertical totals-card__group--total">
                      <div className="totals-card__label">Total to date</div>
                      <div className="totals-card__values">
                        <span className="totals-card__hours">{overallTotals.hours.toFixed(2)} hrs</span>
                        <span className="totals-card__amount">{formatCurrency(overallTotals.amount)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="time-employee-list">
                    {employeeAggregates.map(emp => (
                      <div key={emp.employee_id} className="time-employee-row">
                        <span className="time-employee-row__name">{emp.employee_name}</span>
                        <span className="time-employee-row__hours">{emp.hours.toFixed(2)} hrs</span>
                        <span className="time-employee-row__amount">{formatCurrency(emp.total_value)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="time-allocations-list">
                    {allocations.map(a => {
                      const isSelected = selected.has(a.id);
                      return (
                        <label
                          key={a.id}
                          className={`time-allocation-row${isSelected ? ' time-allocation-row--selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            className="time-allocation-row__checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelected(a.id)}
                          />
                          <div className="time-allocation-row__body">
                            <div className="time-allocation-row__line time-allocation-row__line--top">
                              <span className="time-allocation-row__date">{formatDate(a.date)}</span>
                              {a.employee_type && (
                                <span className="time-allocation-row__role">{a.employee_type}</span>
                              )}
                            </div>
                            <div className="time-allocation-row__line time-allocation-row__line--meta">
                              <span className="time-allocation-row__employee">{a.employee_name}</span>
                              <span className="time-allocation-row__hours">{a.hours.toFixed(2)} hrs</span>
                              <span className="time-allocation-row__rate">{formatCurrency(a.charge_rate)}</span>
                              <span className="time-allocation-row__total">{formatCurrency(a.total_value)}</span>
                            </div>
                            {a.description && (
                              <div className="time-allocation-row__description">{a.description}</div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {selected.size > 0 && (
                    <div className="time-selection-footer">
                      <div className="time-selection-footer__header">
                        <span className="time-selection-footer__label">
                          {selected.size} selected
                        </span>
                        <button
                          type="button"
                          className="time-selection-footer__clear"
                          onClick={clearSelection}
                        >
                          Clear
                        </button>
                      </div>
                      <div className="time-selection-footer__totals">
                        <span>{selectedTotals.hours.toFixed(2)} hrs</span>
                        <span>{formatCurrency(selectedTotals.amount)}</span>
                      </div>
                      <div className="time-selection-footer__employees">
                        {selectedEmployeeAggregates.map(emp => (
                          <div key={emp.employee_id} className="time-selection-footer__employee">
                            <span>{emp.employee_name}</span>
                            <span>{emp.hours.toFixed(2)} hrs</span>
                            <span>{formatCurrency(emp.total_value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="sidebar-section">
              <h3 className="sidebar-section__title">Project Invoices</h3>
              {loading ? (
                <p className="sidebar-section__hint">Loading…</p>
              ) : allInvoices.length === 0 ? (
                <p className="sidebar-section__hint">No invoices for this project/stage yet.</p>
              ) : (
                <>
                  <div className="totals-card">
                    {invoiceStatusAggregates.map(s => (
                      <div key={s.status} className="totals-card__group">
                        <div className="totals-card__label">{s.status}</div>
                        <div className="totals-card__values">
                          <span className="totals-card__amount">{formatCurrency(s.amount)}</span>
                        </div>
                      </div>
                    ))}
                    <div className="totals-card__group totals-card__group--total">
                      <div className="totals-card__label">Total invoiced</div>
                      <div className="totals-card__values">
                        <span className="totals-card__amount">{formatCurrency(invoiceTotal)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="prior-invoice-list">
                    {allInvoices.map(inv => {
                      const isCurrent = currentInvoice !== null && inv.id === currentInvoice.id;
                      const bundledInThis =
                        currentInvoiceId !== null && inv.bundled_in_invoice_id === currentInvoiceId;
                      const bundledElsewhere =
                        inv.bundled_in_invoice_id != null && !bundledInThis;
                      const canBundleHere =
                        !isCurrent && !bundledInThis && !bundledElsewhere && inv.status === 'Draft';
                      return (
                        <div
                          key={inv.id}
                          className={`prior-invoice-row${isCurrent ? ' prior-invoice-row--current' : ''}${bundledInThis ? ' prior-invoice-row--bundled-here' : ''}`}
                          onClick={isCurrent ? undefined : () => handlePriorInvoiceClick(inv.id)}
                          role={isCurrent ? undefined : 'button'}
                          tabIndex={isCurrent ? -1 : 0}
                          onKeyDown={(e) => {
                            if (!isCurrent && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              handlePriorInvoiceClick(inv.id);
                            }
                          }}
                        >
                          <div className="prior-invoice-row__top">
                            <span className="prior-invoice-row__number">
                              {inv.invoice_number}
                              {isCurrent && <span className="prior-invoice-row__current-tag">Current</span>}
                              {bundledInThis && <span className="prior-invoice-row__current-tag">Bundled</span>}
                              {bundledElsewhere && <span className="prior-invoice-row__current-tag">Bundled →</span>}
                            </span>
                            <span className={`status-badge status-${inv.status.toLowerCase().replace(/\s+/g, '-')}`}>
                              {inv.status}
                            </span>
                          </div>
                          <div className="prior-invoice-row__bottom">
                            <span className="prior-invoice-row__date">{formatDate(inv.invoice_date)}</span>
                            <span className="prior-invoice-row__total">{formatCurrency(exGstAmount(inv))}</span>
                          </div>
                          {(canBundleHere || bundledInThis) && currentInvoiceId !== null && (
                            <div className="prior-invoice-row__actions">
                              {canBundleHere && (
                                <button
                                  type="button"
                                  className="prior-invoice-row__action"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void setBundling(inv.id, currentInvoiceId);
                                  }}
                                >
                                  Bundle into this
                                </button>
                              )}
                              {bundledInThis && (
                                <button
                                  type="button"
                                  className="prior-invoice-row__action prior-invoice-row__action--secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void setBundling(inv.id, null);
                                  }}
                                >
                                  Unbundle
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeftSidebar;
